import { SnCPropertyEnum } from "../../constants/NavbarList";
import {
  ExcelBoolean,
  ExcelBooleanString,
  ExcelEmptyDefault,
} from "../constants/defaults";
import {
  SnCExcelFileName,
  SnCExcelHeader,
  SnCExcelSheetName,
  SnCExcelSourceField,
} from "../enums/sncExcel";
import {
  sncExcelColumn,
  sncExcelFile,
  sncExcelSheet,
  sncExcelWorksheet,
} from "../enums/typedExcel";
import {
  mapSnCCatalogItemRows,
  mapSnCFunctionRoomRows,
  mapSnCHolidayRows,
  mapSnCMenuItemRows,
  mapSnCMenuMeasurementRows,
  mapSnCMenuRevenueTypeRows,
  mapSnCRevenueTypeRows,
} from "../mapping/sncItemRows";
import {
  mapRetailItemRows,
  retailItemExcelColumns,
} from "../mapping/retailItemRows";
import {
  excelColumn,
  ExcelExportColumn,
  ExcelExportSheetConfig,
} from "../types";

const sncCodeNameColumns = (
  codeField: SnCExcelSourceField,
  nameField: SnCExcelSourceField,
): ExcelExportColumn[] => [
  sncExcelColumn(SnCExcelHeader.Id, SnCExcelSourceField.Id),
  sncExcelColumn(SnCExcelHeader.Code, codeField),
  sncExcelColumn(SnCExcelHeader.Name, nameField),
  sncExcelColumn(
    SnCExcelHeader.IsActive,
    SnCExcelSourceField.Active,
    ExcelBooleanString.True,
  ),
];

const sncCatalogMainColumns = (
  codeField: SnCExcelSourceField,
  nameField: SnCExcelSourceField,
): ExcelExportColumn[] => [
  sncExcelColumn(
    SnCExcelHeader.ExcelGroupNumber,
    SnCExcelSourceField.ExcelGroupNumber,
  ),
  sncExcelColumn(SnCExcelHeader.ItemCode, codeField),
  sncExcelColumn(SnCExcelHeader.NameUpper, nameField),
  sncExcelColumn(
    SnCExcelHeader.IsActiveUpper,
    SnCExcelSourceField.Active,
    ExcelBooleanString.True,
  ),
  sncExcelColumn(
    SnCExcelHeader.Category,
    ExcelEmptyDefault.Empty,
    ExcelEmptyDefault.Empty,
  ),
  sncExcelColumn(
    SnCExcelHeader.Location,
    ExcelEmptyDefault.Empty,
    ExcelEmptyDefault.Empty,
  ),
  sncExcelColumn(SnCExcelHeader.Quantity, ExcelEmptyDefault.Empty, 0),
  sncExcelColumn(
    SnCExcelHeader.HideQuantity,
    ExcelEmptyDefault.Empty,
    ExcelBooleanString.False,
  ),
  sncExcelColumn(
    SnCExcelHeader.TrackQuantity,
    ExcelEmptyDefault.Empty,
    ExcelBooleanString.False,
  ),
  sncExcelColumn(SnCExcelHeader.Price, ExcelEmptyDefault.Empty, 0),
  sncExcelColumn(
    SnCExcelHeader.HidePrice,
    ExcelEmptyDefault.Empty,
    ExcelBooleanString.False,
  ),
  sncExcelColumn(SnCExcelHeader.Cost, ExcelEmptyDefault.Empty, 0),
  sncExcelColumn(SnCExcelHeader.Gratuity, ExcelEmptyDefault.Empty, 0),
  sncExcelColumn(SnCExcelHeader.ServiceCharge, ExcelEmptyDefault.Empty, 0),
  sncExcelColumn(
    SnCExcelHeader.CommentsUpper,
    ExcelEmptyDefault.Empty,
    ExcelEmptyDefault.Empty,
  ),
  sncExcelColumn(
    SnCExcelHeader.VendorName,
    ExcelEmptyDefault.Empty,
    ExcelEmptyDefault.Empty,
  ),
  sncExcelColumn(
    SnCExcelHeader.ContactType,
    ExcelEmptyDefault.Empty,
    ExcelEmptyDefault.Empty,
  ),
  sncExcelColumn(
    SnCExcelHeader.PhoneNumber,
    ExcelEmptyDefault.Empty,
    ExcelEmptyDefault.Empty,
  ),
  sncExcelColumn(
    SnCExcelHeader.EmailType,
    ExcelEmptyDefault.Empty,
    ExcelEmptyDefault.Empty,
  ),
  sncExcelColumn(
    SnCExcelHeader.EmailAddress,
    ExcelEmptyDefault.Empty,
    ExcelEmptyDefault.Empty,
  ),
  sncExcelColumn(
    SnCExcelHeader.Address1,
    ExcelEmptyDefault.Empty,
    ExcelEmptyDefault.Empty,
  ),
  sncExcelColumn(
    SnCExcelHeader.Address2,
    ExcelEmptyDefault.Empty,
    ExcelEmptyDefault.Empty,
  ),
  sncExcelColumn(
    SnCExcelHeader.Address3,
    ExcelEmptyDefault.Empty,
    ExcelEmptyDefault.Empty,
  ),
  sncExcelColumn(
    SnCExcelHeader.City,
    ExcelEmptyDefault.Empty,
    ExcelEmptyDefault.Empty,
  ),
  sncExcelColumn(
    SnCExcelHeader.State,
    ExcelEmptyDefault.Empty,
    ExcelEmptyDefault.Empty,
  ),
  sncExcelColumn(
    SnCExcelHeader.Country,
    ExcelEmptyDefault.Empty,
    ExcelEmptyDefault.Empty,
  ),
  sncExcelColumn(
    SnCExcelHeader.PostalCode,
    ExcelEmptyDefault.Empty,
    ExcelEmptyDefault.Empty,
  ),
  sncExcelColumn(SnCExcelHeader.Id, ExcelEmptyDefault.Empty, 0),
];

const sncCatalogMeasurementColumns = (
  codeField: SnCExcelSourceField,
): ExcelExportColumn[] => [
  sncExcelColumn(
    SnCExcelHeader.ExcelGroupNumber,
    SnCExcelSourceField.ExcelGroupNumber,
  ),
  sncExcelColumn(SnCExcelHeader.ItemCode, codeField),
  sncExcelColumn(
    SnCExcelHeader.UnitOfMeasureCode,
    ExcelEmptyDefault.Empty,
    ExcelEmptyDefault.Empty,
  ),
  sncExcelColumn(SnCExcelHeader.UnitCost, ExcelEmptyDefault.Empty, 0),
  sncExcelColumn(SnCExcelHeader.UnitPrice, ExcelEmptyDefault.Empty, 0),
];

/** FunctionItemRevenuetype / catalog revenue sheets — matches Visual One columns. */
const sncCatalogRevenueTypeColumns = (
  codeField: SnCExcelSourceField,
  revenueItemField: SnCExcelSourceField,
): ExcelExportColumn[] => [
  sncExcelColumn(
    SnCExcelHeader.ExcelGroupNumber,
    SnCExcelSourceField.ExcelGroupNumber,
  ),
  sncExcelColumn(SnCExcelHeader.ItemCode, codeField),
  sncExcelColumn(SnCExcelHeader.ItemNumber, revenueItemField),
  sncExcelColumn(SnCExcelHeader.BuildingCode, SnCExcelSourceField.Building),
];

/**
 * Sales & Catering Excel export definitions — mapped to SnC onboarding payload keys.
 * File / worksheet / column names follow the Visual One import template spec.
 */
export const sncExcelExportSheets: ExcelExportSheetConfig[] = [
  // Retail foundation (same Settings columns as Golf/Spa).
  sncExcelSheet(SnCPropertyEnum.outletSetup, SnCExcelFileName.Outlet, [
    sncExcelColumn(
      SnCExcelHeader.IdPascal,
      ExcelEmptyDefault.Empty,
      ExcelEmptyDefault.Empty,
    ),
    sncExcelColumn(SnCExcelHeader.OutletName, SnCExcelSourceField.OutletName),
    sncExcelColumn(
      SnCExcelHeader.ProfitCenter,
      ExcelEmptyDefault.Empty,
      ExcelEmptyDefault.Empty,
    ),
    sncExcelColumn(
      SnCExcelHeader.IsActivePascal,
      SnCExcelSourceField.Active,
      ExcelBooleanString.True,
    ),
    sncExcelColumn(
      SnCExcelHeader.TerminalId,
      ExcelEmptyDefault.Empty,
      ExcelEmptyDefault.Empty,
    ),
    sncExcelColumn(
      SnCExcelHeader.AutoReceipt,
      SnCExcelSourceField.AutoPrintsReceipts,
      ExcelBooleanString.True,
    ),
  ], SnCExcelSheetName.Outlet),

  sncExcelSheet(
    SnCPropertyEnum.categoryGroups,
    SnCExcelFileName.CategoryGroup,
    [
      sncExcelColumn(
        SnCExcelHeader.IdPascal,
        ExcelEmptyDefault.Empty,
        ExcelEmptyDefault.Empty,
      ),
      sncExcelColumn(
        SnCExcelHeader.NamePascal,
        SnCExcelSourceField.CategoryGroup,
      ),
      sncExcelColumn(
        SnCExcelHeader.IsActivePascal,
        SnCExcelSourceField.Active,
        ExcelBoolean.True,
      ),
    ],
    SnCExcelSheetName.CategoryGroup,
  ),

  sncExcelSheet(
    SnCPropertyEnum.retailCategories,
    SnCExcelFileName.Category,
    [
      sncExcelColumn(
        SnCExcelHeader.IdPascal,
        ExcelEmptyDefault.Empty,
        ExcelEmptyDefault.Empty,
      ),
      sncExcelColumn(
        SnCExcelHeader.NamePascal,
        SnCExcelSourceField.CategoryName,
      ),
      sncExcelColumn(
        SnCExcelHeader.EligibleForDiscount,
        SnCExcelSourceField.EligibleForDiscount,
        ExcelBooleanString.False,
      ),
      sncExcelColumn(
        SnCExcelHeader.EligibleForCostOrCostPlus,
        SnCExcelSourceField.EligibleForCostOrCostPlus,
        ExcelBooleanString.False,
      ),
      sncExcelColumn(
        SnCExcelHeader.EligibleForInHouseServiceChargeCalculation,
        SnCExcelSourceField.EligibleForInHouseServiceChargeCalculation,
        ExcelBooleanString.False,
      ),
      sncExcelColumn(
        SnCExcelHeader.IsActivePascal,
        SnCExcelSourceField.Active,
        ExcelBoolean.True,
      ),
      sncExcelColumn(
        SnCExcelHeader.CategoryGroupName,
        SnCExcelSourceField.CategoryGroup,
        ExcelEmptyDefault.Empty,
      ),
    ],
    SnCExcelSheetName.Category,
  ),

  sncExcelSheet(
    SnCPropertyEnum.arrivalTypes,
    SnCExcelFileName.ArrivalTypes,
    [
      sncExcelColumn(
        SnCExcelHeader.Id,
        ExcelEmptyDefault.Empty,
        ExcelEmptyDefault.Empty,
      ),
      sncExcelColumn(SnCExcelHeader.Code, SnCExcelSourceField.ArrivalTypeCode),
      sncExcelColumn(SnCExcelHeader.Name, SnCExcelSourceField.ArrivalTypeName),
      sncExcelColumn(
        SnCExcelHeader.IsActive,
        ExcelEmptyDefault.Empty,
        ExcelBooleanString.True,
      ),
    ],
    SnCExcelSheetName.ArrivalType,
  ),

  // Sheets / columns match Visual One AudioVisualItem workbook exactly.
  sncExcelFile(
    SnCPropertyEnum.audioVisualItems,
    SnCExcelFileName.AudioVisualItems,
    [
      sncExcelWorksheet(
        SnCExcelSheetName.AudioVisualItem,
        sncCatalogMainColumns(
          SnCExcelSourceField.AudioVisualCode,
          SnCExcelSourceField.AudioVisualName,
        ),
        false,
        mapSnCCatalogItemRows(SnCExcelSourceField.AudioVisualCode),
      ),
      sncExcelWorksheet(
        SnCExcelSheetName.AudioVisualItemMeasurement,
        sncCatalogMeasurementColumns(SnCExcelSourceField.AudioVisualCode),
        true,
      ),
      sncExcelWorksheet(
        SnCExcelSheetName.AudioVisualItemRevenuetype,
        sncCatalogRevenueTypeColumns(
          SnCExcelSourceField.AudioVisualCode,
          SnCExcelSourceField.RevenueItem,
        ),
        false,
        mapSnCRevenueTypeRows(
          SnCExcelSourceField.AudioVisualCode,
          SnCExcelSourceField.RevenueItem,
        ),
        undefined,
        true,
      ),
    ],
  ),

  // Sheets / columns match Visual One BeverageItem workbook exactly.
  sncExcelFile(
    SnCPropertyEnum.beverageItems,
    SnCExcelFileName.BeverageItems,
    [
      sncExcelWorksheet(
        SnCExcelSheetName.BeverageItem,
        sncCatalogMainColumns(
          SnCExcelSourceField.BeverageCode,
          SnCExcelSourceField.BeverageName,
        ),
        false,
        mapSnCCatalogItemRows(SnCExcelSourceField.BeverageCode),
      ),
      sncExcelWorksheet(
        SnCExcelSheetName.BeverageItemMeasurement,
        sncCatalogMeasurementColumns(SnCExcelSourceField.BeverageCode),
        true,
      ),
      sncExcelWorksheet(
        SnCExcelSheetName.BeverageItemRevenuetype,
        sncCatalogRevenueTypeColumns(
          SnCExcelSourceField.BeverageCode,
          SnCExcelSourceField.RevenueItem,
        ),
        false,
        mapSnCRevenueTypeRows(
          SnCExcelSourceField.BeverageCode,
          SnCExcelSourceField.RevenueItem,
        ),
        undefined,
        true,
      ),
    ],
  ),

  sncExcelSheet(
    SnCPropertyEnum.cancelCodes,
    SnCExcelFileName.CancelCodes,
    [
      sncExcelColumn(
        SnCExcelHeader.Id,
        ExcelEmptyDefault.Empty,
        ExcelEmptyDefault.Empty,
      ),
      sncExcelColumn(SnCExcelHeader.Code, SnCExcelSourceField.CancelCode),
      sncExcelColumn(SnCExcelHeader.Name, SnCExcelSourceField.CancelCodeName),
      sncExcelColumn(
        SnCExcelHeader.IsActive,
        ExcelEmptyDefault.Empty,
        ExcelBooleanString.True,
      ),
    ],
    SnCExcelSheetName.CancellationCodes,
  ),

  sncExcelSheet(
    SnCPropertyEnum.cancellationPolicy,
    SnCExcelFileName.CancellationPolicy,
    [
      sncExcelColumn(
        SnCExcelHeader.Id,
        ExcelEmptyDefault.Empty,
        ExcelEmptyDefault.Empty,
      ),
      sncExcelColumn(
        SnCExcelHeader.Code,
        SnCExcelSourceField.CancellationPolicyCode,
      ),
      sncExcelColumn(
        SnCExcelHeader.Name,
        SnCExcelSourceField.CancellationPolicyName,
      ),
      sncExcelColumn(
        SnCExcelHeader.Comments,
        SnCExcelSourceField.CancellationPolicy,
      ),
      sncExcelColumn(
        SnCExcelHeader.IsActive,
        ExcelEmptyDefault.Empty,
        ExcelBooleanString.True,
      ),
    ],
    SnCExcelSheetName.CancellationPolicy,
  ),

  sncExcelSheet(
    SnCPropertyEnum.contactType,
    SnCExcelFileName.ContactTypes,
    [
      sncExcelColumn(
        SnCExcelHeader.Id,
        ExcelEmptyDefault.Empty,
        ExcelEmptyDefault.Empty,
      ),
      sncExcelColumn(SnCExcelHeader.Code, SnCExcelSourceField.ContactTypeCode),
      sncExcelColumn(SnCExcelHeader.Name, SnCExcelSourceField.ContactTypeName),
      sncExcelColumn(
        SnCExcelHeader.IsActive,
        ExcelEmptyDefault.Empty,
        ExcelBooleanString.True,
      ),
    ],
    SnCExcelSheetName.ContactType,
  ),

  sncExcelSheet(
    SnCPropertyEnum.denialCodes,
    SnCExcelFileName.DenialCodes,
    [
      sncExcelColumn(
        SnCExcelHeader.Id,
        ExcelEmptyDefault.Empty,
        ExcelEmptyDefault.Empty,
      ),
      sncExcelColumn(SnCExcelHeader.Code, SnCExcelSourceField.DenialCode),
      sncExcelColumn(SnCExcelHeader.Name, SnCExcelSourceField.DenialCodeName),
      sncExcelColumn(
        SnCExcelHeader.IsActive,
        ExcelEmptyDefault.Empty,
        ExcelBooleanString.True,
      ),
    ],
    SnCExcelSheetName.DenialCode,
  ),

  sncExcelSheet(
    SnCPropertyEnum.distributionList,
    SnCExcelFileName.DistributionListCodes,
    [
      sncExcelColumn(
        SnCExcelHeader.Id,
        ExcelEmptyDefault.Empty,
        ExcelEmptyDefault.Empty,
      ),
      sncExcelColumn(
        SnCExcelHeader.Code,
        SnCExcelSourceField.DistributionListCode,
      ),
      sncExcelColumn(
        SnCExcelHeader.Name,
        SnCExcelSourceField.DistributionListName,
      ),
      sncExcelColumn(
        SnCExcelHeader.IsActive,
        ExcelEmptyDefault.Empty,
        ExcelBooleanString.True,
      ),
    ],
    SnCExcelSheetName.DistributionListCode,
  ),

  sncExcelSheet(
    SnCPropertyEnum.foodTypes,
    SnCExcelFileName.FoodTypes,
    [
      sncExcelColumn(
        SnCExcelHeader.Id,
        ExcelEmptyDefault.Empty,
        ExcelEmptyDefault.Empty,
      ),
      sncExcelColumn(SnCExcelHeader.Code, SnCExcelSourceField.FoodTypeCode),
      sncExcelColumn(SnCExcelHeader.Name, SnCExcelSourceField.FoodTypeName),
      sncExcelColumn(
        SnCExcelHeader.IsActive,
        ExcelEmptyDefault.Empty,
        ExcelBooleanString.True,
      ),
    ],
    SnCExcelSheetName.FoodType,
  ),

  sncExcelSheet(
    SnCPropertyEnum.functionRoomsFeatures,
    SnCExcelFileName.FunctionRoomFeature,
    [
      sncExcelColumn(
        SnCExcelHeader.Id,
        ExcelEmptyDefault.Empty,
        ExcelEmptyDefault.Empty,
      ),
      sncExcelColumn(SnCExcelHeader.Code, SnCExcelSourceField.FeatureCode),
      sncExcelColumn(SnCExcelHeader.Name, SnCExcelSourceField.FeatureName),
      sncExcelColumn(
        SnCExcelHeader.IsActive,
        ExcelEmptyDefault.Empty,
        ExcelBooleanString.True,
      ),
    ],
    SnCExcelSheetName.FunctionRoomFeature,
  ),

  sncExcelSheet(
    SnCPropertyEnum.functionRoomTypeFee,
    SnCExcelFileName.FunctionRoomFeeType,
    [
      sncExcelColumn(
        SnCExcelHeader.Id,
        ExcelEmptyDefault.Empty,
        ExcelEmptyDefault.Empty,
      ),
      sncExcelColumn(SnCExcelHeader.Code, SnCExcelSourceField.FeeTypeCode),
      sncExcelColumn(SnCExcelHeader.Name, SnCExcelSourceField.FeeTypeName),
      sncExcelColumn(
        SnCExcelHeader.IsActive,
        ExcelEmptyDefault.Empty,
        ExcelBooleanString.True,
      ),
    ],
    SnCExcelSheetName.FunctionRoomFee,
  ),

  sncExcelSheet(
    SnCPropertyEnum.functionRoomSetup,
    SnCExcelFileName.FunctionRoomSetup,
    [
      sncExcelColumn(
        SnCExcelHeader.Id,
        ExcelEmptyDefault.Empty,
        ExcelEmptyDefault.Empty,
      ),
      sncExcelColumn(SnCExcelHeader.Code, SnCExcelSourceField.SetupStyleCode),
      sncExcelColumn(SnCExcelHeader.Name, SnCExcelSourceField.SetupStyleName),
      sncExcelColumn(
        SnCExcelHeader.IsActive,
        ExcelEmptyDefault.Empty,
        ExcelBooleanString.True,
      ),
    ],
    SnCExcelSheetName.FunctionRoomSetup,
  ),

  // Sheet / columns match Visual One "Function Room" template exactly.
  sncExcelSheet(
    SnCPropertyEnum.functionRoom,
    SnCExcelFileName.FunctionRoom,
    [
      excelColumn("id", ExcelEmptyDefault.Empty, ExcelEmptyDefault.Empty),
      excelColumn("ROOM_CODE", SnCExcelSourceField.FunctionRoomCode),
      excelColumn("ROOM_NAME", SnCExcelSourceField.FunctionRoomName),
      excelColumn(
        "ROOM_DESCRIPTION",
        ExcelEmptyDefault.Empty,
        ExcelEmptyDefault.Empty,
      ),
      excelColumn("LENGTH", ExcelEmptyDefault.Empty, 0),
      excelColumn("WIDTH", ExcelEmptyDefault.Empty, 0),
      excelColumn("HEIGHT", ExcelEmptyDefault.Empty, 0),
      excelColumn("RENT", ExcelEmptyDefault.Empty, 0),
      excelColumn("SQUAREFEET", ExcelEmptyDefault.Empty, 0),
      excelColumn("BUILDING_CODE", SnCExcelSourceField.Building),
      excelColumn("REVENUE_ITEM_NUMBER", SnCExcelSourceField.RevenueType),
      excelColumn(
        "IS_OVERBOOK_ALLOWED",
        ExcelEmptyDefault.Empty,
        ExcelBooleanString.False,
      ),
      excelColumn(
        "IS_MULTI_ROOM",
        ExcelEmptyDefault.Empty,
        ExcelBooleanString.False,
      ),
      excelColumn("LINKED_ROOM_CODES", ExcelEmptyDefault.Empty, "[]"),
      excelColumn(
        "ROOM_FEATURE_CODES",
        SnCExcelSourceField.RoomFeatures,
        "[]",
      ),
      excelColumn(
        "ISACTIVE",
        ExcelEmptyDefault.Empty,
        ExcelBooleanString.True,
      ),
    ],
    SnCExcelSheetName.FunctionRoom,
    mapSnCFunctionRoomRows,
  ),

  // Sheet / columns must match Visual One "Function Type" template exactly.
  sncExcelSheet(
    SnCPropertyEnum.functionTypes,
    SnCExcelFileName.FunctionType,
    [
      excelColumn("id", ExcelEmptyDefault.Empty, ExcelEmptyDefault.Empty),
      excelColumn(
        "FUNCTION_TYPE_CODE",
        SnCExcelSourceField.FunctionTypeCode,
      ),
      excelColumn(
        "FUNCTION_TYPE_NAME",
        SnCExcelSourceField.FunctionTypeCodeName,
      ),
      excelColumn("SETUP_CODE", ExcelEmptyDefault.Empty, ExcelEmptyDefault.Empty),
      excelColumn(
        "COUNT_COVERS",
        ExcelEmptyDefault.Empty,
        ExcelBooleanString.True,
      ),
      excelColumn(
        "INCLD_ON_REPORT",
        ExcelEmptyDefault.Empty,
        ExcelBooleanString.True,
      ),
      excelColumn("START_TIME", ExcelEmptyDefault.Empty, "00:00"),
      excelColumn("END_TIME", ExcelEmptyDefault.Empty, "01:00"),
      excelColumn("AVG_FOOD", ExcelEmptyDefault.Empty, 0),
      excelColumn("AVG_BEVERAGE", ExcelEmptyDefault.Empty, 0),
      excelColumn("AVG_AUDIO_VISUAL", ExcelEmptyDefault.Empty, 0),
      excelColumn("AVG_MISCELLANEOUS", ExcelEmptyDefault.Empty, 0),
      excelColumn(
        "ISACTIVE",
        ExcelEmptyDefault.Empty,
        ExcelBooleanString.True,
      ),
    ],
    SnCExcelSheetName.FunctionType,
  ),

  // Sheet / columns match Visual One "Holiday" template exactly.
  sncExcelSheet(
    SnCPropertyEnum.holidaysAndHotDates,
    SnCExcelFileName.HolidayAndHotDates,
    [
      excelColumn("id", ExcelEmptyDefault.Empty, ExcelEmptyDefault.Empty),
      excelColumn("code", SnCExcelSourceField.HolidayHotDateCode),
      excelColumn("name", SnCExcelSourceField.HolidayHotDateName),
      excelColumn("lineType", SnCExcelSourceField.DisplayLine),
      excelColumn("dispLocation", SnCExcelSourceField.DisplayLocation),
      excelColumn("colorCode", ExcelEmptyDefault.Empty, "#000000"),
      excelColumn(
        "isActive",
        ExcelEmptyDefault.Empty,
        ExcelBooleanString.True,
      ),
      excelColumn("startDate", SnCExcelSourceField.StartDateField),
      excelColumn("endDate", SnCExcelSourceField.EndDateField),
    ],
    SnCExcelSheetName.Holiday,
    mapSnCHolidayRows,
  ),

  sncExcelSheet(
    SnCPropertyEnum.incidentals,
    SnCExcelFileName.Incidentals,
    [
      sncExcelColumn(SnCExcelHeader.Code, SnCExcelSourceField.IncidentalCode),
      sncExcelColumn(SnCExcelHeader.Name, SnCExcelSourceField.IncidentalName),
      sncExcelColumn(
        SnCExcelHeader.IsActive,
        ExcelEmptyDefault.Empty,
        ExcelBooleanString.True,
      ),
      sncExcelColumn(
        SnCExcelHeader.Id,
        ExcelEmptyDefault.Empty,
        ExcelEmptyDefault.Empty,
      ),
    ],
    SnCExcelSheetName.Incidentals,
  ),

  sncExcelSheet(
    SnCPropertyEnum.retailItems,
    SnCExcelFileName.RetailItems,
    retailItemExcelColumns,
    SnCExcelSheetName.RetailItem,
    mapRetailItemRows,
  ),

  sncExcelSheet(
    SnCPropertyEnum.managers,
    SnCExcelFileName.Managers,
    [
      sncExcelColumn(
        SnCExcelHeader.ManagerGroupNumber,
        ExcelEmptyDefault.Empty,
        ExcelEmptyDefault.Empty,
      ),
      sncExcelColumn(
        SnCExcelHeader.MappingCode,
        SnCExcelSourceField.MappingCode,
      ),
      sncExcelColumn(
        SnCExcelHeader.ManagerType,
        SnCExcelSourceField.ManagerType,
      ),
      sncExcelColumn(
        SnCExcelHeader.IsActiveUpper,
        SnCExcelSourceField.IsActiveManager,
        ExcelBooleanString.True,
      ),
      sncExcelColumn(
        SnCExcelHeader.ManagerFirstName,
        SnCExcelSourceField.ManagerFirstName,
      ),
      sncExcelColumn(
        SnCExcelHeader.ManagerLastName,
        SnCExcelSourceField.ManagerLastName,
      ),
    ],
    SnCExcelSheetName.Managers,
  ),

  sncExcelFile(SnCPropertyEnum.menuItems, SnCExcelFileName.MenuItems, [
    sncExcelWorksheet(
      SnCExcelSheetName.MenuItem,
      [
        sncExcelColumn(
          SnCExcelHeader.MenuItemCode,
          SnCExcelSourceField.MenuItemCode,
        ),
        sncExcelColumn(
          SnCExcelHeader.MenuItemName,
          SnCExcelSourceField.MenuItemName,
        ),
        sncExcelColumn(
          SnCExcelHeader.IsActiveUpper,
          SnCExcelSourceField.Active,
          ExcelBooleanString.True,
        ),
        sncExcelColumn(SnCExcelHeader.Gratuity, ExcelEmptyDefault.Empty, 0),
        sncExcelColumn(
          SnCExcelHeader.ServiceCharge,
          ExcelEmptyDefault.Empty,
          0,
        ),
        sncExcelColumn(
          SnCExcelHeader.FoodTypeCodes,
          SnCExcelSourceField.FoodTypes,
        ),
        sncExcelColumn(
          SnCExcelHeader.Presentation,
          ExcelEmptyDefault.Empty,
          ExcelEmptyDefault.Empty,
        ),
        sncExcelColumn(
          SnCExcelHeader.IdPascal,
          ExcelEmptyDefault.Empty,
          ExcelEmptyDefault.Empty,
        ),
      ],
      false,
      mapSnCMenuItemRows,
    ),
    sncExcelWorksheet(
      SnCExcelSheetName.MenuItemMeasurement,
      [
        sncExcelColumn(
          SnCExcelHeader.MenuItemCode,
          SnCExcelSourceField.MenuItemCode,
        ),
        sncExcelColumn(
          SnCExcelHeader.MenuItemName,
          SnCExcelSourceField.MenuItemName,
        ),
        sncExcelColumn(
          SnCExcelHeader.UnitOfMeasureCodeUnderscore,
          SnCExcelSourceField.UnitOfMeasure,
        ),
        sncExcelColumn(SnCExcelHeader.Cost, ExcelEmptyDefault.Empty, 0),
        sncExcelColumn(SnCExcelHeader.Price, ExcelEmptyDefault.Empty, 0),
        sncExcelColumn(
          SnCExcelHeader.CountByCovers,
          ExcelEmptyDefault.Empty,
          ExcelBooleanString.False,
        ),
      ],
      false,
      mapSnCMenuMeasurementRows,
      undefined,
      true,
    ),
    sncExcelWorksheet(
      SnCExcelSheetName.MenuItemRevenueType,
      [
        sncExcelColumn(
          SnCExcelHeader.MenuItemCode,
          SnCExcelSourceField.MenuItemCode,
        ),
        sncExcelColumn(
          SnCExcelHeader.MenuItemName,
          SnCExcelSourceField.MenuItemName,
        ),
        sncExcelColumn(
          SnCExcelHeader.BuildingCode,
          SnCExcelSourceField.Building,
        ),
        sncExcelColumn(
          SnCExcelHeader.RetailItemNumber,
          SnCExcelSourceField.ItemCode,
        ),
        sncExcelColumn(
          SnCExcelHeader.PropertyCode,
          ExcelEmptyDefault.Empty,
          ExcelEmptyDefault.Empty,
        ),
      ],
      false,
      mapSnCMenuRevenueTypeRows,
      undefined,
      true,
    ),
  ]),

  // Sheets / columns match Visual One MiscellaneousItem workbook exactly.
  sncExcelFile(
    SnCPropertyEnum.miscellaneousItems,
    SnCExcelFileName.MiscellaneousItem,
    [
      sncExcelWorksheet(
        SnCExcelSheetName.MiscellaneousItem,
        sncCatalogMainColumns(
          SnCExcelSourceField.MiscellaneousCode,
          SnCExcelSourceField.MiscellaneousName,
        ),
        false,
        mapSnCCatalogItemRows(SnCExcelSourceField.MiscellaneousCode),
      ),
      sncExcelWorksheet(
        SnCExcelSheetName.MiscellaneousItemMeasurement,
        sncCatalogMeasurementColumns(SnCExcelSourceField.MiscellaneousCode),
        true,
      ),
      sncExcelWorksheet(
        SnCExcelSheetName.MiscellaneousItemRevenuetype,
        sncCatalogRevenueTypeColumns(
          SnCExcelSourceField.MiscellaneousCode,
          SnCExcelSourceField.RevenueItem,
        ),
        false,
        mapSnCRevenueTypeRows(
          SnCExcelSourceField.MiscellaneousCode,
          SnCExcelSourceField.RevenueItem,
        ),
        undefined,
        true,
      ),
    ],
  ),

  sncExcelSheet(
    SnCPropertyEnum.potentialCodes,
    SnCExcelFileName.PotentialCodes,
    [
      sncExcelColumn(
        SnCExcelHeader.Id,
        ExcelEmptyDefault.Empty,
        ExcelEmptyDefault.Empty,
      ),
      sncExcelColumn(SnCExcelHeader.Code, SnCExcelSourceField.PotentialCode),
      sncExcelColumn(
        SnCExcelHeader.Name,
        SnCExcelSourceField.PotentialCodeName,
      ),
      sncExcelColumn(
        SnCExcelHeader.IsActive,
        ExcelEmptyDefault.Empty,
        ExcelBooleanString.True,
      ),
    ],
    SnCExcelSheetName.PotentialCode,
  ),

  sncExcelSheet(
    SnCPropertyEnum.reservedByCodes,
    SnCExcelFileName.ReservedByCodes,
    [
      sncExcelColumn(
        SnCExcelHeader.Id,
        ExcelEmptyDefault.Empty,
        ExcelEmptyDefault.Empty,
      ),
      sncExcelColumn(SnCExcelHeader.Code, SnCExcelSourceField.ReservedByCode),
      sncExcelColumn(SnCExcelHeader.Name, SnCExcelSourceField.ReservedByName),
      sncExcelColumn(
        SnCExcelHeader.IsActive,
        ExcelEmptyDefault.Empty,
        ExcelBooleanString.True,
      ),
    ],
    SnCExcelSheetName.ReservedByCode,
  ),

  sncExcelSheet(
    SnCPropertyEnum.roomAndTax,
    SnCExcelFileName.RoomAndTax,
    [
      sncExcelColumn(SnCExcelHeader.Code, SnCExcelSourceField.RoomAndTaxCode),
      sncExcelColumn(SnCExcelHeader.Name, SnCExcelSourceField.RoomAndTaxName),
      sncExcelColumn(
        SnCExcelHeader.IsActive,
        ExcelEmptyDefault.Empty,
        ExcelBooleanString.True,
      ),
      sncExcelColumn(
        SnCExcelHeader.Id,
        ExcelEmptyDefault.Empty,
        ExcelEmptyDefault.Empty,
      ),
    ],
    SnCExcelSheetName.RoomAndTax,
  ),

  sncExcelSheet(
    SnCPropertyEnum.salesCallsCallTypes,
    SnCExcelFileName.SalesCallCallTypes,
    sncCodeNameColumns(
      SnCExcelSourceField.CallTypeCode,
      SnCExcelSourceField.CallTypeName,
    ),
    SnCExcelSheetName.SalesCallType
  ),

  sncExcelSheet(
    SnCPropertyEnum.salesCallsNextSteps,
    SnCExcelFileName.SalesCallsNextSteps,
    sncCodeNameColumns(
      SnCExcelSourceField.NextStepCode,
      SnCExcelSourceField.NextStepName,
    ),
    SnCExcelSheetName.SalesCallStep
  ),

  sncExcelFile(SnCPropertyEnum.setupItems, SnCExcelFileName.SetupItems, [
    sncExcelWorksheet(
      SnCExcelSheetName.SetupItem,
      sncCatalogMainColumns(
        SnCExcelSourceField.SetupCode,
        SnCExcelSourceField.SetupName,
      ),
      false,
      mapSnCCatalogItemRows(SnCExcelSourceField.SetupCode),
    ),
    sncExcelWorksheet(
      SnCExcelSheetName.FunctionItemMeasurement,
      sncCatalogMeasurementColumns(SnCExcelSourceField.SetupCode),
      true,
    ),
    sncExcelWorksheet(
      SnCExcelSheetName.FunctionItemRevenuetype,
      sncCatalogRevenueTypeColumns(
        SnCExcelSourceField.SetupCode,
        SnCExcelSourceField.RevenueItem,
      ),
      false,
      mapSnCRevenueTypeRows(
        SnCExcelSourceField.SetupCode,
        SnCExcelSourceField.RevenueItem,
      ),
      undefined,
      true,
    ),
  ]),

  sncExcelSheet(
    SnCPropertyEnum.unitsOfMeasure,
    SnCExcelFileName.UnitsOfMeasure,
    [
      sncExcelColumn(
        SnCExcelHeader.Id,
        ExcelEmptyDefault.Empty,
        ExcelEmptyDefault.Empty,
      ),
      sncExcelColumn(
        SnCExcelHeader.Code,
        SnCExcelSourceField.UnitOfMeasureCode,
      ),
      sncExcelColumn(
        SnCExcelHeader.Name,
        SnCExcelSourceField.UnitOfMeasureName,
      ),
      sncExcelColumn(
        SnCExcelHeader.IsActive,
        ExcelEmptyDefault.Empty,
        ExcelBooleanString.True,
      ),
    ],
    SnCExcelSheetName.UnitOfMeasure,
  ),
];
